import React from 'react';
import { useStudioTypeSize } from '../../StudioTypeScale';
import { resolveTwoColumnContent } from '../../../../utils/contentCopy';
import { colorWithAlpha } from '../../../../utils/color';
import EditorialFrame, {
  type EditorialInjectedProps,
  type EditorialLayoutProps,
  type EditorialTokens
} from './EditorialFrame';

const EditorialColumn: React.FC<{
  title: string;
  items: string[];
  isStory?: boolean;
  tokens: EditorialTokens;
}> = ({ title, items, isStory, tokens: t }) => {
  const typeSize = useStudioTypeSize();
  return (
  <div
    style={{
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      border: `1px solid ${t.line}`,
      borderRadius: isStory ? 13 : 10,
      background: t.panel,
      boxShadow: '0 9px 22px rgba(32, 35, 31, 0.07)'
    }}
  >
    <div
      style={{
        padding: isStory ? '10px 11px' : '8px 9px',
        color: t.ink,
        background: colorWithAlpha(t.accent, 0.12),
        borderBottom: `2px solid ${t.accent}`,
        fontFamily: t.serif,
        fontSize: typeSize(isStory ? 13 : 11),
        fontWeight: 700,
        letterSpacing: '-0.2px',
        lineHeight: 1.25
      }}
    >
      {title}
    </div>
    <div style={{ padding: isStory ? '6px 12px 10px' : '5px 10px 8px' }}>
      {items.map((item, index) => (
        <div
          key={`${index}-${item}`}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: isStory ? 8 : 6,
            padding: isStory ? '9px 0' : '7px 0',
            borderBottom: index === items.length - 1 ? 'none' : `1px solid ${t.line}`,
            color: t.ink,
            fontFamily: t.serif,
            fontSize: typeSize(isStory ? 12.5 : 10.5),
            lineHeight: 1.42
          }}
        >
          <span
            style={{
              width: 5,
              height: 5,
              marginTop: isStory ? 7 : 6,
              borderRadius: '50%',
              background: t.accent,
              flexShrink: 0
            }}
          />
          <span>{item}</span>
        </div>
      ))}
    </div>
  </div>
  );
};

const EditorialTwoColumnsContent: React.FC<EditorialInjectedProps & { content: any }> = ({
  content,
  tokens,
  isStory,
  locale
}) => {
  const t = tokens!;
  const typeSize = useStudioTypeSize();
  const resolved = resolveTwoColumnContent(content, locale);

  return (
    <div>
      <h2
        style={{
          margin: '0 auto 20px',
          textAlign: 'center',
          color: t.ink,
          fontFamily: t.serif,
          fontSize: typeSize(isStory ? 32 : 25),
          letterSpacing: '-0.8px',
          lineHeight: 1.13
        }}
      >
        {resolved.title}
      </h2>
      <div style={{ display: 'flex', gap: isStory ? 13 : 10, alignItems: 'stretch' }}>
        <EditorialColumn title={resolved.leftTitle} items={resolved.leftItems} isStory={isStory} tokens={t} />
        <EditorialColumn title={resolved.rightTitle} items={resolved.rightItems} isStory={isStory} tokens={t} />
      </div>
    </div>
  );
};

const EditorialTwoColumnsLayout: React.FC<EditorialLayoutProps> = props => (
  <EditorialFrame {...props}>
    <EditorialTwoColumnsContent content={props.content || {}} />
  </EditorialFrame>
);

export default EditorialTwoColumnsLayout;
